import { useEffect, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { config } from '../config';
import { useGetAssetThumbnailUrlQuery } from '../services';
import { useGetSlideThumbnailQuery } from '../services/slides.service';
import { FolderManagerMimeTypes } from '../interfaces';

interface UseItemThumbnailParams {
  itemId?: number | string;
  itemType?: string;
  thumbnailUrl?: string | null;
  themeId?: string | number | null;
  assetId?: number;
  enabled?: boolean;
}

interface UseItemThumbnailResult {
  thumbnailUrl: string | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isThemeChanging: boolean;
  refetch: () => void;
}

const SKIPPED_TYPES = [
  FolderManagerMimeTypes.FOLDER,
  FolderManagerMimeTypes.WORKSPACE,
];

export const useItemThumbnail = ({
  itemId,
  itemType,
  thumbnailUrl: slideThumbnailBaseUrl,
  themeId,
  assetId,
  enabled = true,
}: UseItemThumbnailParams): UseItemThumbnailResult => {
  const isSlide = itemType === FolderManagerMimeTypes.SLIDE;
  const isSkippedType = !itemType || SKIPPED_TYPES.includes(itemType as FolderManagerMimeTypes);
  const normalizedThemeId = themeId || null;

  // track theme changes
  const prevThemeIdRef = useRef(normalizedThemeId);
  const [isThemeChanging, setIsThemeChanging] = useState(false);

  // poll while the server is generating the themed thumbnail (202 response)
  const [slidePollInterval, setSlidePollInterval] = useState(0);

  // determine the asset ID for the query
  const assetIdForQuery = assetId || (typeof itemId === 'number' ? itemId : undefined);

  // asset thumbnail query (for non-slide assets)
  const shouldSkipAssetQuery = !enabled || isSlide || isSkippedType || !assetIdForQuery;

  const {
    data: assetThumbnailUrl,
    isLoading: isAssetLoading,
    isFetching: isAssetFetching,
    refetch: refetchAsset,
  } = useGetAssetThumbnailUrlQuery(
    assetIdForQuery ? { assetId: assetIdForQuery } : skipToken,
    { skip: shouldSkipAssetQuery }
  );

  // slide thumbnail query (for slides only)
  const {
    data: slideThumbnailData,
    isLoading: isSlideLoading,
    isFetching: isSlideFetching,
    isError: isSlideError,
    refetch: refetchSlide,
  } = useGetSlideThumbnailQuery(
    !enabled ? skipToken :
    isSlide && slideThumbnailBaseUrl
      ? { url: slideThumbnailBaseUrl, themeId: normalizedThemeId }
      : skipToken,
    { pollingInterval: slidePollInterval }
  );

  const isFetching = isSlide ? isSlideFetching : isAssetFetching;
  const isSlideGenerating = slideThumbnailData?.isGenerating ?? false;

  useEffect(() => {
    setSlidePollInterval(isSlideGenerating ? config.defaultFetchInterval : 0);
  }, [isSlideGenerating]);

  useEffect(() => {
    if (prevThemeIdRef.current !== normalizedThemeId) {
      setIsThemeChanging(true);
      prevThemeIdRef.current = normalizedThemeId;
    }
  }, [normalizedThemeId]);

  useEffect(() => {
    if (isThemeChanging && !isFetching) {
      setIsThemeChanging(false);
    }
  }, [isThemeChanging, isFetching]);

  if (isSlide) {
    return {
      thumbnailUrl: isSlideError ? undefined : slideThumbnailData?.url,
      isLoading: isSlideLoading,
      isFetching: isSlideFetching,
      isThemeChanging,
      refetch: refetchSlide,
    };
  }

  return {
    thumbnailUrl: assetThumbnailUrl,
    isLoading: isAssetLoading,
    isFetching: isAssetFetching,
    isThemeChanging,
    refetch: refetchAsset,
  };
};
